/**
 * Rule Run show — full trace for one rule evaluation on the shared Inspector
 * grammar (design-system §5 / frontend-entity-coverage §2.3).
 *
 * Per the matrix a rule run must expose: event_id, rule_id, artifact_id, error,
 * guard_result, effect_result, context snapshot, plus a **retry** action and a
 * **disable-rule** action:
 *
 * - Retry        → re-emits the originating event (POST /api/events/:id/replay),
 *                  which re-runs the kernel idempotently (UNIQUE(event_id,rule_id)).
 * - Disable rule → PATCH /api/rules/:id { status: 'disabled' }.
 * - Context      → fetched from rule_runs.context_snapshot_url, shown as JSON.
 *
 * Layout: Overview (identity/timing) · Body (guard / effect / error / context
 * packet) · Relations (Rule / Event / Artifact / spawned AgentRun + EffectRun
 * teaser) · Audit (generic timeline). EffectRun is `[PROPOSED]` — it renders a
 * labeled-empty teaser inside the Body, never an error.
 */
import { useShow, useNavigation, useGo, type HttpError } from "@refinedev/core";
import { Ban, Bot, FileBox, Repeat, ScrollText, Zap } from "@/components/ui/icon";
import type { RuleRun } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { JsonViewer } from "@/components/app/JsonViewer";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  JsonOrText,
  RefLink,
  RelationsBlock,
  AuditBlock,
  type RelationGroup,
  TechnicalId,
} from "@/components/inspector";
import { ShowPageSkeleton, ShowPageError } from "@/components/inspector/ShowPage";
import {
  formatDateTime,
  parseMaybeJson,
  useRemoteContent,
} from "../_shared/runs-ui";
import { useApiAction } from "../_shared/useApiAction";
import { ProposedShowTeaser } from "../_shared/ProposedModule";

function ContextSnapshot({ url }: { url: string | null }) {
  const state = useRemoteContent(url);
  if (!url) {
    return <p className="text-sm text-muted-foreground">No context snapshot.</p>;
  }
  if (state.status === "loading") {
    return (
      <p className="text-sm text-muted-foreground">Loading context packet…</p>
    );
  }
  if (state.status === "error") {
    return (
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">
          Could not load snapshot ({state.error}).
        </p>
        <p className="font-mono text-xs text-muted-foreground">{url}</p>
      </div>
    );
  }
  if (state.status === "success") {
    const { value, isJson } = parseMaybeJson(state.text);
    return isJson ? (
      <JsonViewer data={value} />
    ) : (
      <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs">
        {state.text}
      </pre>
    );
  }
  return null;
}

export function RuleRunShow() {
  const { query } = useShow<RuleRun, HttpError>();
  const { list } = useNavigation();
  const go = useGo();
  const { run, loading } = useApiAction();

  const record = query.data?.data;

  if (query.isLoading) return <ShowPageSkeleton />;
  if (query.isError || !record) {
    return (
      <ShowPageError
        message={query.error?.message ?? "Rule run not found."}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const retry = () => {
    void run(`/events/${record.event_id}/replay`, {
      invalidateResources: ["rule-runs", "events", "agent-runs"],
      successMessage: "Originating event re-emitted; rule re-evaluated.",
    });
  };

  const disableRule = () => {
    void run(`/rules/${record.rule_id}`, {
      method: "PATCH",
      body: { status: "disabled" },
      invalidateResources: ["rules", "rule-runs"],
      successMessage: "Rule disabled.",
    });
  };

  const relations: RelationGroup[] = [
    {
      title: "References",
      items: [
        { label: "Rule", icon: ScrollText, resource: "rules", id: record.rule_id },
        { label: "Event", icon: Zap, resource: "events", id: record.event_id },
        {
          label: "Artifact",
          icon: FileBox,
          resource: "artifacts",
          id: record.artifact_id,
        },
      ],
    },
    {
      title: "Spawned",
      emptyMessage: "No agent runs spawned by this evaluation.",
      items: [
        {
          label: "Agent run",
          icon: Bot,
          node: (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                go({ to: { resource: "agent-runs", action: "list" } });
              }}
              className="cursor-pointer text-primary hover:underline"
            >
              View agent runs
            </a>
          ),
        },
      ],
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title="Rule Run"
        subtitle="One trace record per rule evaluation — its effects, status, and actions."
        status={record.status}
        primaryAction={
          <Button
            size="sm"
            onClick={retry}
            disabled={loading}
            data-testid="rule-run-retry"
          >
            <Repeat className="mr-2 h-4 w-4" />
            {loading ? "Working…" : "Retry"}
          </Button>
        }
        actions={
          <Button variant="ghost" size="sm" onClick={() => list("rule-runs")}>
            Back
          </Button>
        }
        menuActions={[
          {
            label: "Disable rule",
            icon: Ban,
            destructive: true,
            disabled: loading,
            onClick: disableRule,
          },
        ]}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Rule">
            <RefLink resource="rules" id={record.rule_id} />
          </Field>
          <Field label="Rule version">{record.rule_version}</Field>
          <Field label="Started">{formatDateTime(record.started_at)}</Field>
          <Field label="Finished">{formatDateTime(record.finished_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Body">
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Guard result
              </h4>
              <JsonOrText value={record.guard_result} />
            </div>
            <div className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Effect result
              </h4>
              <JsonOrText value={record.effect_result} />
            </div>
          </div>

          {record.error ? (
            <div className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wide text-destructive">
                Error
              </h4>
              <pre className="whitespace-pre-wrap rounded-md bg-destructive/10 p-3 font-mono text-xs text-destructive">
                {record.error}
              </pre>
            </div>
          ) : null}

          <div className="space-y-2">
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Effect runs
            </h4>
            <ProposedShowTeaser endpoint="/api/effect-runs" />
          </div>

          <div className="space-y-2">
            <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Context packet
            </h4>
            <ContextSnapshot url={record.context_snapshot_url} />
          </div>
        </div>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock groups={relations} />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={String(record.id)} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock
          entityType="rule-runs"
          entityId={record.id}
          provenance={[
            { label: "Rule", resource: "rules", id: record.rule_id },
            { label: "Event", resource: "events", id: record.event_id },
          ]}
        />
      </InspectorSection>
    </Inspector>
  );
}

export default RuleRunShow;
